import {
  OverlayScrollbarsComponent,
  type OverlayScrollbarsComponentProps,
  type OverlayScrollbarsComponentRef,
} from "overlayscrollbars-react";
import { forwardRef, type ReactNode } from "react";

/**
 * Wraps content in an OverlayScrollbars instance so scroll regions use the
 * app's custom themed scrollbars (see `.os-scrollbar` in GlobalStyles) instead
 * of the native ones — the same scrollbars the tables get via
 * `StyledTableScroll`. Constrain the scroll area with `max-height`/`max-width`
 * (e.g. via a `styled(OverlayScroll)`); OverlayScrollbars manages the overflow.
 *
 * The optional ref exposes the OverlayScrollbars component handle — use
 * `ref.osInstance()?.elements().viewport` for imperative scrolling (e.g.
 * resetting to the top when the content is replaced).
 *
 * Built on `overlayscrollbars-react` (not a bare `OverlayScrollbars(el)` call)
 * so the scroll viewport is React-rendered: React keeps ownership of the DOM,
 * which is required when the wrapped content swaps (e.g. the page container
 * around `<Routes>`) — an imperative init relocates children and then crashes
 * React's reconciler with a `removeChild` error.
 */
export const OverlayScroll = forwardRef<
  OverlayScrollbarsComponentRef,
  {
    className?: string;
    children: ReactNode;
    /** OverlayScrollbars event listeners — note init is deferred (idle
     *  callback), so `initialized` fires well after mount under load. */
    events?: OverlayScrollbarsComponentProps["events"];
  }
>(function OverlayScroll({ className, children, events }, ref) {
  return (
    <OverlayScrollbarsComponent
      ref={ref}
      element="div"
      className={className}
      events={events}
      defer
    >
      {children}
    </OverlayScrollbarsComponent>
  );
});
